import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { AttributePrototypesResponse } from "@/models/api/administration/servicesAndAttributes/AttributePrototype";
import { AttributePrototypeAddEdit } from "@/models/api/administration/servicesAndAttributes/AttributePrototypeAddEdit";
import {
  AttributePrototypeMapperResponse,
  MapperSuggestion,
} from "@/models/api/administration/servicesAndAttributes/AttributePrototypeMapper";

//#region Routes
export const URL_GET_ATTRIBUTE_PROTOTYPES_PAGED = "/Attribute/Prototype/Paged";
export const URL_POST_REMOVE_ATTRIBUTE_PROTOTYPE = "/Attribute/Prototype/${AttributePrototypeId}/Remove";
export const URL_POST_LOCK_ATTRIBUTE_PROTOTYPE = "/Attribute/Prototype/${AttributePrototypeId}/Lock";
export const URL_POST_UNLOCK_ATTRIBUTE_PROTOTYPE = "/Attribute/Prototype/${AttributePrototypeId}/Unlock";
export const URL_POST_ADD_ATTRIBUTE_PROTOTYPE = "/Attribute/Prototype/Add";
export const URL_POST_EDIT_ATTRIBUTE_PROTOTYPE = "/Attribute/Prototype/${AttributePrototypeId}/Edit";
export const URL_GET_ATTRIBUTE_PROTOTYPE_MAPPERS = "/Attribute/Prototype/${AttributePrototypeId}/Mapper";
export const URL_GET_MAPPER_SUGGESTIONS = "/Attribute/Prototype/${AttributePrototypeId}/Mapper/Suggestions";
export const URL_POST_ADD_ATTRIBUTE_PROTOTYPE_MAPPERS_FROM_SUGGESTIONS =
  "/Attribute/Prototype/${AttributePrototypeId}/Mapper/Add/Suggestions";
export const URL_POST_REMOVE_ATTRIBUTE_PROTOTYPE_MAPPER =
  "/Attribute/Prototype/${AttributePrototypeId}/Mapper/${AttributeMapperId}/Remove";
export const URL_POST_EDIT_ATTRIBUTE_PROTOTYPE_MAPPER =
  "/Attribute/Prototype/${AttributePrototypeId}/Mapper/${AttributeMapperId}/Edit";
export const URL_POST_ADD_ATTRIBUTE_PROTOTYPE_MAPPER =
  "/Attribute/Prototype/${AttributePrototypeId}/Mapper/${AttributeMapperId}/Add";
export const URL_POST_ADD_ATTRIBUTE_PROTOTYPE_MAPPERS = "/Attribute/Prototype/${AttributePrototypeId}/Mapper/Add";
//#endregion

const _getAttributePrototypes = new CancellableHelper<AttributePrototypesResponse>(
  URL_GET_ATTRIBUTE_PROTOTYPES_PAGED,
  AttributePrototypesResponse
);
_getAttributePrototypes.jsonSerializer = true;
export const getAttributePrototypes = async (
  genericTableRequest: GenericTableRequest
): Promise<AttributePrototypesResponse> => {
  return await _getAttributePrototypes.get(genericTableRequest);
};

export const removeAttributePrototype = async (attributePrototypeId: number): Promise<null> => {
  const _removeAttributePrototype = new CancellableHelper<null>(URL_POST_REMOVE_ATTRIBUTE_PROTOTYPE, null, true);
  return await _removeAttributePrototype.post({ AttributePrototypeId: attributePrototypeId }, null);
};

export const lockAttributePrototype = async (attributePrototypeId: number): Promise<null> => {
  const _lockAttributePrototype = new CancellableHelper<null>(URL_POST_LOCK_ATTRIBUTE_PROTOTYPE, null, true);
  return await _lockAttributePrototype.post({ AttributePrototypeId: attributePrototypeId }, null);
};

export const unlockAttributePrototype = async (attributePrototypeId: number): Promise<null> => {
  const _unlockAttributePrototype = new CancellableHelper<null>(URL_POST_UNLOCK_ATTRIBUTE_PROTOTYPE, null, true);
  return await _unlockAttributePrototype.post({ AttributePrototypeId: attributePrototypeId }, null);
};

const _addAttributePrototype = new CancellableHelper<null>(URL_POST_ADD_ATTRIBUTE_PROTOTYPE, null, true);
export const addAttributePrototype = async (attributePrototypeAddEdit: AttributePrototypeAddEdit): Promise<null> => {
  return await _addAttributePrototype.post({}, attributePrototypeAddEdit);
};

const _editAttributePrototype = new CancellableHelper<null>(URL_POST_EDIT_ATTRIBUTE_PROTOTYPE, null, true);
export const editAttributePrototype = async (
  attributePrototypeAddEdit: AttributePrototypeAddEdit,
  attributePrototypeId: number
): Promise<null> => {
  return await _editAttributePrototype.post({ AttributePrototypeId: attributePrototypeId }, attributePrototypeAddEdit);
};

const _getAttributePrototypeMappers = new CancellableHelper<AttributePrototypeMapperResponse>(
  URL_GET_ATTRIBUTE_PROTOTYPE_MAPPERS,
  AttributePrototypeMapperResponse
);
_getAttributePrototypeMappers.jsonSerializer = true;
export const getAttributePrototypeMappers = async (
  attributePrototypeId: number,
  genericTableRequest: GenericTableRequest
): Promise<AttributePrototypeMapperResponse> => {
  return await _getAttributePrototypeMappers.get({
    AttributePrototypeId: attributePrototypeId,
    ...genericTableRequest,
  });
};

const _getMapperSuggestions = new CancellableHelper<Array<MapperSuggestion>>(
  URL_GET_MAPPER_SUGGESTIONS,
  MapperSuggestion
);
export const getMapperSuggestions = async (attributePrototypeId: number): Promise<Array<MapperSuggestion>> => {
  return await _getMapperSuggestions.get({
    AttributePrototypeId: attributePrototypeId,
  });
};

const _addAttributePrototypeMappersFromSuggestions = new CancellableHelper<null>(
  URL_POST_ADD_ATTRIBUTE_PROTOTYPE_MAPPERS_FROM_SUGGESTIONS,
  null,
  true
);
export const addAttributePrototypeMappersFromSuggestions = async (
  attributePrototypeId: number,
  suggestions: Array<MapperSuggestion>
): Promise<null> => {
  return await _addAttributePrototypeMappersFromSuggestions.post(
    { AttributePrototypeId: attributePrototypeId },
    suggestions
  );
};

const _removeAttributePrototypeMapper = new CancellableHelper<null>(
  URL_POST_REMOVE_ATTRIBUTE_PROTOTYPE_MAPPER,
  null,
  true
);
export const removeAttributePrototypeMapper = async (
  attributePrototypeId: number,
  attributeMapperId: number
): Promise<null> => {
  return await _removeAttributePrototypeMapper.post({
    AttributePrototypeId: attributePrototypeId,
    AttributeMapperId: attributeMapperId,
  });
};

const _addAttributePrototypeMapper = new CancellableHelper<null>(URL_POST_ADD_ATTRIBUTE_PROTOTYPE_MAPPER, null, true);
export const addAttributePrototypeMapper = async (
  attributePrototypeId: number,
  model: MapperSuggestion
): Promise<null> => {
  return await _addAttributePrototypeMapper.post({ AttributePrototypeId: attributePrototypeId }, model);
};

const _addAttributePrototypeMappers = new CancellableHelper<null>(URL_POST_ADD_ATTRIBUTE_PROTOTYPE_MAPPERS, null, true);
export const addAttributePrototypeMappers = async (
  attributePrototypeId: number,
  attributeMappersIds: Array<number>
): Promise<null> => {
  return await _addAttributePrototypeMappers.post(
    { AttributePrototypeId: attributePrototypeId },
    { MapperAttributeId: attributeMappersIds }
  );
};

const _editAttributePrototypeMapper = new CancellableHelper<null>(URL_POST_EDIT_ATTRIBUTE_PROTOTYPE_MAPPER, null, true);
export const editAttributePrototypeMapper = async (
  attributePrototypeId: number,
  model: MapperSuggestion,
  attributeMapperId: number
): Promise<null> => {
  return await _editAttributePrototypeMapper.post(
    {
      AttributePrototypeId: attributePrototypeId,
      AttributeMapperId: attributeMapperId,
    },
    model
  );
};
